import axios from 'axios';
import { disconnectSocket } from './socket';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  timeout: 30000,
});

// Attach token from localStorage
const token = localStorage.getItem('token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Credential-flow endpoints whose 401s mean "bad credentials / dead session",
// not "expired access token" — they must reach the caller (so e.g. a failed
// login renders its error instead of hard-reloading the page). /auth/me is
// deliberately NOT here: it authenticates like any other route and should go
// through the refresh path on expiry.
const CREDENTIAL_PATHS = /\/auth\/(login|register|refresh|logout|forgot-password|reset-password)/;

// Single-flight: parallel 401s (a dashboard mount fires several requests)
// share ONE refresh call. With server-side rotation live, concurrent
// refreshes would consume each other's tokens and log the user out.
let refreshPromise = null;

function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) return Promise.reject(new Error('No refresh token'));
  refreshPromise = api.post('/auth/refresh', { refreshToken })
    .then(res => {
      const newToken = res.data.token;
      localStorage.setItem('token', newToken);
      // The server rotates the refresh token on every use — store the
      // replacement or the next refresh presents a dead token.
      if (res.data.refreshToken) {
        localStorage.setItem('refreshToken', res.data.refreshToken);
      }
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      return newToken;
    })
    .finally(() => { refreshPromise = null; });
  return refreshPromise;
}

function hardLogout() {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  // Don't leave a stashed privileged admin token behind if an impersonated
  // session dies through this path.
  localStorage.removeItem('adminToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('impersonatingEmail');
  disconnectSocket();
  window.location.href = '/login';
}

// Response interceptor: handle 401 with token refresh
api.interceptors.response.use(
  res => res,
  async err => {
    const original = err.config;
    if (
      err.response?.status === 401 &&
      original &&
      !original._retry &&
      !CREDENTIAL_PATHS.test(original.url || '')
    ) {
      original._retry = true;
      try {
        const newToken = await refreshAccessToken();
        original.headers['Authorization'] = `Bearer ${newToken}`;
        return api(original);
      } catch (_) {
        hardLogout();
      }
    }
    return Promise.reject(err);
  }
);

export default api;
